import * as THREE from 'three';

// Procedural normal maps from tiled value-noise height fields (no external assets). The
// noise wraps so the texture repeats seamlessly. Normals are encoded in LINEAR color space.

function hash(x: number, y: number, seed: number): number {
  let h = (x * 374761393 + y * 668265263 + seed * 69069) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

// Value noise tiled over `period` cells so it repeats without seams.
function valueNoise(x: number, y: number, period: number, seed: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const x0 = ((xi % period) + period) % period;
  const y0 = ((yi % period) + period) % period;
  const x1 = (x0 + 1) % period;
  const y1 = (y0 + 1) % period;
  const a = hash(x0, y0, seed);
  const b = hash(x1, y0, seed);
  const c = hash(x0, y1, seed);
  const d = hash(x1, y1, seed);
  const u = smooth(xf);
  const v = smooth(yf);
  return THREE.MathUtils.lerp(THREE.MathUtils.lerp(a, b, u), THREE.MathUtils.lerp(c, d, u), v);
}

function fbm(x: number, y: number, octaves: number, basePeriod: number, seed: number): number {
  let amp = 1;
  let freq = 1;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise(x * freq, y * freq, basePeriod * freq, seed + o);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return sum / norm;
}

export interface NormalOpts {
  size?: number;
  octaves?: number;
  period?: number; // noise cells across the texture
  strength?: number; // bump strength
  seed?: number;
}

// A seamless, repeatable normal map (RepeatWrapping, NoColorSpace).
export function makeNormalTexture(opts: NormalOpts = {}): THREE.CanvasTexture {
  const size = opts.size ?? 256;
  const octaves = opts.octaves ?? 4;
  const period = opts.period ?? 8;
  const strength = opts.strength ?? 2.2;
  const seed = opts.seed ?? 1;

  const h = new Float32Array(size * size);
  const scale = period / size;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      h[y * size + x] = fbm(x * scale, y * scale, octaves, period, seed);
    }
  }
  const at = (x: number, y: number): number =>
    h[(((y % size) + size) % size) * size + (((x % size) + size) % size)];

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Sobel-style gradient (wrapped) -> tangent-space normal.
      const gx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const gy = (at(x, y + 1) - at(x, y - 1)) * strength;
      const nx = -gx;
      const ny = -gy;
      const nz = 1;
      const inv = 1 / Math.hypot(nx, ny, nz);
      const i = (y * size + x) * 4;
      img.data[i] = (nx * inv * 0.5 + 0.5) * 255;
      img.data[i + 1] = (ny * inv * 0.5 + 0.5) * 255;
      img.data[i + 2] = (nz * inv * 0.5 + 0.5) * 255;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.generateMipmaps = true;
  return tex;
}
